import { Storage } from "@/services/Storage";
import axios from "axios";
import { responses } from "@/utils/responses";
import router from "@/router";
import connectivityMonitor from '@/services/connectivityMonitor';
import loginOutbox from '@/services/loginOutbox';
import { supportStaffGate } from '@/services/supportStaffGate';
import * as Sentry from "@sentry/vue";
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { assertSalesWriteUser } from '@/services/salesWriteUser';

const AUTO_MAIN_SERVER_CUSTOMER_ID = '9721';

export default {
    name: 'user',
    state: {
        admin: '',
        currentUser: {
            Type: "",
            Username: "",
            id: "",
            Surname: "",
            Name: "",
            Password: ""
        },
        securities: [],
        users: [],
        allUsers: [],
        supervisor: null,
    },
    getters: {
        getCurrentUser: (state) => state.currentUser,
        getAllUsers: (state) => state.allUsers,
        getAdmin: (state) => {
            if (state.admin) {
                return JSON.parse(window.atob(state.admin))
            }
        },
        getUsers: (state) => state.users,
        getUserByPass: (state) => async (password) => {
            // DEPRECATED after password-encryption rollout (2026-06-18).
            // The cached users list no longer carries `Password`, so this
            // compare always returns undefined. Use `dispatch('posLogin')`
            // (backend PIN verification) instead.
            console.warn('[user_module] getUserByPass is deprecated after the password-encryption rollout. Use dispatch("posLogin").');
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.Password && u.Password.toLowerCase() === password.toLowerCase());
        },

        countNonLytSoftUsers: async (state) => {
            state.users = await Storage.getAllUsers();
            return state.users.filter(user => user.Username !== 'Admin').length;
        },

        getUserByID: (state) => async (id) => {
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.id === id);
        },
        getSecurities: (state) => state.securities,
        getSupervisor: (state) => state.supervisor,
    },
    mutations: {
        setAdmin: async (state, data) => {
            state.admin = window.btoa(JSON.stringify(data));
        },
        setCurrentUser: async (state, data) => {
            state.currentUser = data;
        },
        setAllSecurities: async (state, data) => {
            if (!data) data = await Storage.getAllSecurities();
            state.securities = data;
        },
        setAllUsers: async (state, data) => {
            if (!data) {
                data = await Storage.getAllUsers();
            }
            state.users = data
        },
        setSupervisor(state, supervisor) {
            state.supervisor = supervisor;
        },
    },
    actions: {
        async autoSetMainServerOnLogin(context, { store, device, isOnline } = {}) {
            if (!isOnline) return false;
            if (window.androidMode) return false;
            const mainCustomerID = String(store?.mainCustomerID ?? '').trim();
            if (!store || mainCustomerID !== AUTO_MAIN_SERVER_CUSTOMER_ID) return false;
            if (!device) return false;

            try {
                await context.dispatch('updateServerForAll', device);

                const currentSettings = await Storage.getDeviceSettings();
                if (currentSettings && currentSettings.server !== device) {
                    await Storage.setDeviceSettings({
                        ...currentSettings,
                        server: device,
                    });
                }
                return true;
            } catch (err) {
                console.warn('[User Module] Failed to auto-set main server on login:', err?.message || err);
                return false;
            }
        },

        async recordlogin(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            // Fail-fast if cashier identity missing — never omit username (#26328)
            const safeUsername = assertSalesWriteUser(username, 'user_module.recordlogin');
            const payload = {
                username: safeUsername,
                platform: 'RetailPOS',
                pc: device,
            }
            
            // Check if online
            const isOnline = connectivityMonitor.getStatus();

            // Non-blocking: for configured main customer login, set this PC as main server.
            if (isOnline) {
                context.dispatch('autoSetMainServerOnLogin', { store, device, isOnline })
                    .catch((err) => {
                        console.warn('[User Module] Auto main-server login hook failed:', err?.message || err);
                    });
            }
            
            if (!isOnline) {
                // Offline: Queue login record for later sync
                console.log('[User Module] Offline - queueing login record for sync');
                const loginId = await loginOutbox.enqueueLogin(username, 'RetailPOS', device, store.StoreDB);
                if (loginId) {
                    console.log('[User Module] Login record queued:', loginId);
                } else {
                    console.warn('[User Module] Failed to queue login record');
                }
                return;
            }
            
            // Online: Post to server
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogin`, payload)
                .catch(async (err) => {
                    // Network error: Queue for offline sync
                    console.warn('[User Module] Failed to record login, queueing for sync:', err.message);
                    const loginId = await loginOutbox.enqueueLogin(username, 'RetailPOS', device, store.StoreDB);
                    if (loginId) {
                        console.log('[User Module] Login record queued after failure:', loginId);
                    }
                    // Don't throw error - allow login to proceed
                });
        },

        async recordLogout(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username: username,
                platform: 'RetailPOS',
                pc: device,
            }
            // If offline, skip remote call gracefully
            const isOnline = connectivityMonitor.getStatus();
            if (!isOnline) {
                console.log('[User Module] Offline - skipping recordLogout API call');
                // D3: still clear the recorded support staff on logout so we
                // don't attribute any later (none expected, defence in depth)
                // actions to a staff no longer on shift.
                supportStaffGate.clearRecordedSupportAgent();
                return;
            }
            // Online: try to post, swallow errors so logout never blocks
            return axios.post(
                serverApi + `supervisorOverrides/${store.StoreDB}/recordLogout`,
                payload,
                { timeout: 2500 }
            )
                .catch((err) => {
                    console.warn('[User Module] Failed to record logout:', err?.message || err);
                    // Do not throw - allow logout to proceed
                })
                .finally(() => {
                    // D3: clear the recorded support staff on logout so we
                    // don't attribute any later actions to a staff no longer
                    // on shift. Runs whether the post succeeded or failed so
                    // a transient network error at logout time doesn't leave
                    // a stale support agent on record.
                    supportStaffGate.clearRecordedSupportAgent();
                });
        },

        getAllUsers: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            if (!store) {
                let route = 'login';
                let allStores = context.getters.getRegisteredStores;
                if (allStores.length > 0) {
                    route = 'setup';
                }
                responses.showInfo(`Welcome , Please Login`)
                return router.push({ name: route })
            }
            
            // Check if online
            // const isOnline = connectivityMonitor.getStatus();
            //
            // if (!isOnline) {
            //     // Offline: Load from cache
            //     console.log('[User Module] Offline - loading users from cache');
            //     const cachedUsers = await Storage.getAllUsers();
            //     if (cachedUsers && cachedUsers.length > 0) {
            //         context.commit('setAllUsers', cachedUsers);
            //         return cachedUsers;
            //     } else {
            //         responses.showWarning('No cached user data available. Please connect to network.');
            //         return [];
            //     }
            // }
            
            // Online: Fetch from server
            return axios.get(franchiseTomcatApi + `employee/${store.StoreDB}/getAll`)
                .then(async ({ data }) => {
                    const filteredUsers = data.filter(user => !user.archived);
                    await Storage.setAllUsers(filteredUsers);
                    context.commit('setAllUsers', filteredUsers);
                    return data;
                }).catch(async (err) => {
                    // Network error: Fallback to cache
                    console.warn('[User Module] Failed to fetch users from server, using cache');
                    const cachedUsers = await Storage.getAllUsers();
                    if (cachedUsers && cachedUsers.length > 0) {
                        context.commit('setAllUsers', cachedUsers);
                        Toast.fire({
                            icon: 'warning',
                            title: 'Using cached user data'
                        });
                        return cachedUsers;
                    }
                    return errorHandler.tomcatError(err);
                })
        },

        posLogin: async (context, data) => {
            // Backend PIN verification.
            //
            // After the password-encryption rollout (Argon2id hashes stored
            // in emp.Password) the client can no longer compare the typed
            // PIN against the cached users list - hashes are one-way.
            // Verification has to move server-side, so the POS now calls
            // POST {franchiseTomcatApi}/posAuth/{DB}/login which uses
            // PasswordEncryptionService.verify() under the hood.
            //
            // See docs/PASSWORD-ENCRYPTION-ROLLOUT.md for the full contract.
            try {
                let store = await Storage.getDefaultStore();
                // eslint-disable-next-line no-console
                console.log(`[user_module][posLogin] invoked storeDb=${store && store.StoreDB ? store.StoreDB : 'undefined'} hasPassword=${!!data}`);
                if (!store || !store.StoreDB) {
                    // eslint-disable-next-line no-console
                    console.warn('[user_module][posLogin] no default store — bailing out');
                    return { isValid: false, username: null };
                }
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${store.StoreDB}/login`,
                    { password: data }
                );
                // eslint-disable-next-line no-console
                console.log('[user_module][posLogin] backend response', JSON.stringify({
                    success: !!(resp && resp.success),
                    hasSessionId: !!(resp && resp.sessionId),
                    sessionIdPrefix: resp && resp.sessionId ? String(resp.sessionId).slice(0, 8) + '...' : 'none',
                    username: resp && resp.username,
                    empId: resp && resp.empId,
                    hasExpiresAt: !!(resp && resp.sessionExpiresAt),
                }));
                if (!resp || !resp.success) {
                    // eslint-disable-next-line no-console
                    console.warn('[user_module][posLogin] backend rejected login (success=false or no response)');
                    return { isValid: false, username: null };
                }
                // Persist the deeplink session payload so the
                // backoffice-module deeplink opener can build a
                // `LytningCore://open?session=…&store=…&page=…` URL
                // without the plaintext password. Clear any stale
                // session first so a switch between stores on the same
                // device does not leak the previous store's token.
                try {
                    await Storage.clearCurrentSession();
                    await Storage.setCurrentSession({
                        sessionId: resp.sessionId,
                        sessionExpiresAt: resp.sessionExpiresAt,
                        username: resp.username,
                        empId: resp.empId,
                    }, store.StoreDB);
                    // eslint-disable-next-line no-console
                    console.log(`[user_module][posLogin] session persisted storeDb=${store.StoreDB} sessionIdPrefix=${String(resp.sessionId || '').slice(0, 8)}...`);
                } catch (sessionErr) {
                    // Persistence is best-effort - do not block login.
                    // eslint-disable-next-line no-console
                    console.warn('[user_module][posLogin] failed to persist deeplink session', sessionErr);
                }
                const currentUser = context.getters.getUsers.find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (currentUser) {
                    context.commit('setCurrentUser', currentUser);
                    // Attach the authenticated cashier to all subsequent
                    // Sentry events so they are attributable in the Sentry UI.
                    // PII fields (email) are still scrubbed by `redactPII` in
                    // `src/main.js` and `dataCollection.userInfo: false`.
                    Sentry.setUser({
                        id: currentUser.id || currentUser.Username || undefined,
                        username: currentUser.Username || undefined,
                    });
                    context.dispatch('getAllStock');
                    return { isValid: true, username: currentUser.Username };
                }
                // Cache miss - refresh roster from server and retry once.
                await context.dispatch('getAllUsers');
                const refreshed = context.getters.getUsers.find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (refreshed) {
                    context.commit('setCurrentUser', refreshed);
                    context.dispatch('getAllStock');
                    return { isValid: true, username: refreshed.Username };
                }
                return { isValid: true, username: resp.username };
            } catch (err) {
                const status = err && err.response && err.response.status;

                // 404 fallback: server endpoint not registered on this
                // deployment (legacy franchise). Compare the typed PIN
                // locally against the cached users list. Legacy stores
                // that pre-date the Argon2id rollout (2026-06-18) still
                // carry the plaintext Password field and will match;
                // newer stores will fail closed because their cached
                // Password is empty.
                if (status === 404) {
                    const typedPin = data == null ? '' : String(data);
                    const matched = context.getters.getUsers.find(
                        u => u.Password && typeof u.Password === 'string'
                            && u.Password.toLowerCase() === typedPin.toLowerCase()
                    );
                    if (matched) {
                        try {
                            context.commit('setCurrentUser', matched);
                            // Attach the authenticated cashier (legacy fallback
                            // path) to all subsequent Sentry events. PII fields
                            // are still scrubbed by `redactPII` in `src/main.js`
                            // and `dataCollection.userInfo: false`.
                            Sentry.setUser({
                                id: matched.id || matched.Username || undefined,
                                username: matched.Username || undefined,
                            });
                            context.dispatch('getAllStock');
                        } catch (persistErr) {
                            // eslint-disable-next-line no-console
                            console.warn('[user_module][posLogin] legacy fallback: failed to persist current user', persistErr);
                        }
                        return { isValid: true, username: matched.Username };
                    }
                    return { isValid: false, username: null, status };
                }

                return { isValid: false, username: null, status };
            }
        },

        async editEmployee(context, payload) {
            let store = await Storage.getDefaultStore();
            return axios.put(franchiseTomcatApi + `employee/${store.StoreDB}/${payload.id}`, payload)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },

        createEmployee: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `employee/${store.StoreDB}`, payload)
                .then(() => context.dispatch('getAllUsers'))
                .catch((err) => errorHandler.tomcatError(err))
        },

        forceCashUp: async () => {
            let store = await Storage.getDefaultStore();
            let currentUser = await Storage.getCurrentUser()
            let settings = await Storage.getDeviceSettings()
            return axios.get(franchiseTomcatApi + `cashup/${store.StoreDB}/forceCashup?user=` + currentUser.Username + '&pc=' + settings.PC)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        async generateStoreOTP() {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `stores/${store.StoreDB}/generateOTP`)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        validateSecurityPermissionNoPrompt: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                console.warn(`No security group found`)
                return false;
            }
            return group[security];
        },

        validateSecurityPermission: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                await responses.showWarning(`No security group found`)
                return false;
            }
            if (!group[security]) {
                $utils.hideLoading();
                return context.dispatch('checkUserSecurity', security).then(async (result) => {
                    if (result.isConfirmed) {
                        let value = result.value;
                        if (value.allowed) {
                            let store = await Storage.getDefaultStore();
                            await Storage.setSupervisor(value.supervisor);
                            axios.post(serverApi + `supervisorOverrides/${store.StoreDB}`, {
                                Supervisor: value.supervisor,
                                Cashier: value.cashier,
                                Reason: security,
                            })
                        }
                        return value.allowed;
                    }
                    return false;
                });

            }
            if (security.toString().toLowerCase().includes(`void`)) {
                return { supervisor: user.Username }
            }
            return true;
        },

        async getAllSecurities(context) {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            
            // Check if online
            const isOnline = connectivityMonitor.getStatus();
            
            if (!isOnline) {
                // Offline: Load from cache
                console.log('[User Module] Offline - loading securities from cache');
                const cachedSecurities = await Storage.getAllSecurities();
                if (cachedSecurities && cachedSecurities.length > 0) {
                    context.commit('setAllSecurities', cachedSecurities);
                    return cachedSecurities;
                } else {
                    responses.showWarning('No cached security data available. Please connect to network.');
                    return [];
                }
            }
            
            // Online: Fetch from server
            return axios.get(franchiseTomcatApi + `empsecurity/${store.StoreDB}/getAll`)
                .then(async ({ data }) => {
                    await Storage.setAllSecurities(data);
                    context.commit(`setAllSecurities`, data);
                    return data;
                })
                .catch(async (err) => {
                    // Network error: Fallback to cache
                    console.warn('[User Module] Failed to fetch securities from server, using cache');
                    const cachedSecurities = await Storage.getAllSecurities();
                    if (cachedSecurities && cachedSecurities.length > 0) {
                        context.commit('setAllSecurities', cachedSecurities);
                        Toast.fire({
                            icon: 'warning',
                            title: 'Using cached security data'
                        });
                        return cachedSecurities;
                    }
                    return errorHandler.tomcatError(err);
                })
        },

    }
}
